'use client'
import { LinkButton, MediaImageStandard, Typography } from 'components/Contentful'
import { Grid } from '@achieve/ascend'
import { Carousel } from 'components/Carousels'
import { Eyebrow } from 'components/Eyebrow'
import { AchieveLink } from 'components/AchieveLink'
import { getSlug } from 'utils/conversions'
import { AuthorAvatar } from 'components/AuthorAvatar'
import { withUuid } from 'utils/shared'
import {
  AUTHORS_ROUTE,
  AUTHORS_ROUTE_FDR,
  LEARN_ROUTE,
  LEARN_ROUTE_FDR,
} from 'constants/navigation'
import { PlayButtonBackground } from 'components/PlayButtonBackground'
import classNames from 'classnames'
import { mainProductConfig } from 'constants/mainProductConfig'
import { useMemo } from 'react'
import { FdrPage } from '@/utils/environmentConfig'

/**
 * Renders the horizontal especific list of articles
 *
 * @component
 * @param {Object} props.title - The Title of section list.
 * @param {Object} props.relatedArticles - The List of articles. accept query from contenfull client, or graphl structure data
 * @returns {JSX.Element} The rendered component. This component will render a list with a Title and List of articles(three) on desktop and carousel on mobile.
 *
 */
function ArticleListCarousel({
  title = 'Related Articles',
  relatedArticles,
  linkCta,
  link,
  config,
  sectionVariationObject,
  isExternal = false,
  pageHost,
  titleComponent = 'p',
  unifiedArticleLink = false,
}) {
  function RelatedArticleCard({ item, index }) {
    const {
      title,
      slug: articleSlug,
      primaryCategory,
      author,
      mainImage,
      metaDescription,
      readTime,
      contentType,
      fullPath,
    } = item?.fields ?? item ?? {}

    const siteEnv = process.env.NEXT_PUBLIC_SITE_ENVIRONMENT || 'achieve'
    const { slug: authorSlug, authorName, authorPhoto } = author?.fields ?? author ?? {}
    const isVideo = mainImage?.__typename === 'VideoPlayer'
    const isMainPage = contentType === 'Page'
    const baseRoute = isMainPage ? '' : `${LEARN_ROUTE}`

    let linkHrefArticle = `${baseRoute}/${getSlug(primaryCategory)}/${articleSlug}`.replace(
      '//',
      '/'
    )

    if (isMainPage) {
      linkHrefArticle =
        getSlug(primaryCategory) === articleSlug
          ? `/${articleSlug}`
          : `${baseRoute}/${articleSlug}`.replace('//', '/')
    }
    const categorySlug = getSlug(primaryCategory)
    const hrefCategory =
      FdrPage === siteEnv && categorySlug === 'debt-consolidation'
        ? `${LEARN_ROUTE_FDR}/debtconsolidation`
        : `${FdrPage === siteEnv ? LEARN_ROUTE_FDR : baseRoute}/${categorySlug}`

    const LinkItem = ({ children, 'data-testid': dataTestId, className, underline = 'hover' }) => {
      const protocol = typeof window !== 'undefined' ? window.location.protocol : 'https:'
      const host = pageHost || (typeof window !== 'undefined' ? window.location.host : '')
      const fullUrl = isExternal && fullPath ? `${protocol}//${host}/${fullPath}` : linkHrefArticle

      return (
        <AchieveLink
          href={FdrPage === siteEnv && isExternal && fullPath ? `${fullUrl}` : linkHrefArticle}
          track={{
            list_name: `ARTICLE LINK`,
            position: index,
            click_id: `${mainProductConfig[siteEnv || 'achieve']?.['MainProduct']} | ${title}`,
            click_text: title,
            click_url: articleSlug,
            nav_link_section: 'Article',
            track_event: 'link_click',
            click_type: 'Link Click',
            event_action: 'link_click',
            event_type: 'link_click',
            sectionVariationObject,
          }}
          className={classNames('text-[#1d252f]', className)}
          data-testid={dataTestId || 'related-article-card-link'}
          underline={underline}
        >
          {children}
        </AchieveLink>
      )
    }

    const eyebrowsBlue = config?.eyebrowsBlue || false
    const imageContent = (
      <>
        <MediaImageStandard
          objectFit="cover"
          content={
            mainImage?.fields?.mediaContent ?? mainImage?.mediaContent ?? mainImage?.thumbnail
          }
          className={unifiedArticleLink ? 'rounded-xl' : undefined}
          data-testid="related-article-card-image"
        />
        {isVideo && (
          <div
            className={unifiedArticleLink ? 'absolute inset-0' : undefined}
            data-testid="related-article-card-play-button"
          >
            <PlayButtonBackground fontSize={64} />
          </div>
        )}
      </>
    )
    const cardContent = (
      <>
        {mainImage && (
          <Grid size={{ xs: 12 }} className="min-h-[150px] max-h-[220px]">
            <div
              className={
                'relative max-h-full max-w-full w-[335px] h-[210px] lg:w-[390px] lg:h-[219px] [&>span]:box-border [&>span]:block [&>span]:overflow-hidden [&>span]:w-[initial] [&>span]:h-[initial] [&>span]:opacity-100 [&_span]:absolute [&_span]:m-0 [&_span]:p-0 [&_span]:border-0 [&_span]:inset-0 [&_span]:[background:none] [&_a]:[&_img]:absolute [&_a]:[&_img]:box-border [&_a]:[&_img]:block [&_a]:[&_img]:h-10 [&_a]:[&_img]:w-10 [&_a]:[&_img]:max-w-full  [&_a]:[&_img]:max-h-full [&_img]:[&_a]:object-cover[&_img]:[&_a]:object-[center_top] [&_a]:[&_img]:m-auto [&_a]:[&_img]:p-0 [&_a]:[&_img]:rounded-xl [&_a]:[&_img]:border-[none] [&_a]:[&_img]:inset-0'
              }
            >
              {unifiedArticleLink ? (
                imageContent
              ) : (
                <LinkItem data-testid="related-article-card-image-link">{imageContent}</LinkItem>
              )}
            </div>
          </Grid>
        )}
        {readTime && !config?.simplifiedView && (
          <div size={{ xs: 12 }} className="ml-2 mt-6 flex flex-col items-start">
            <Typography
              content={`${readTime} read`}
              variant="bodySm"
              data-testid="related-article-card-read-time"
              className="text-neutral-500 font-normal"
            />
          </div>
        )}
        {!config?.simplifiedView && (
          <Grid size={{ xs: 12 }}>
            {unifiedArticleLink ? (
              <Eyebrow
                className={classNames('text-start pt-2 pb-0', {
                  'text-blue-300': eyebrowsBlue,
                })}
                black
                content={primaryCategory}
                data-testid="related-article-card-eyebrow-text"
              />
            ) : (
              <AchieveLink
                href={hrefCategory}
                className={classNames({ 'text-blue-300': eyebrowsBlue })}
                data-testid="related-article-card-eyebrow-link"
              >
                <Eyebrow
                  className={classNames('text-start pt-2 pb-0', {
                    'text-blue-300': eyebrowsBlue,
                  })}
                  black
                  content={primaryCategory}
                  data-testid="related-article-card-eyebrow-text"
                />
              </AchieveLink>
            )}
          </Grid>
        )}
        {title && (
          <Grid size={{ xs: 12 }} className={config?.simplifiedView && 'pt-6'}>
            {unifiedArticleLink ? (
              <Typography
                content={title}
                variant="headingMd"
                mobileVariant={config?.simplifiedView && 'headingSm'}
                responsive={config?.simplifiedView}
                fontWeight="medium"
                className={classNames({ 'group-hover/card:underline': unifiedArticleLink })}
                data-testid="related-article-card-title"
              />
            ) : (
              <LinkItem data-testid="related-article-card-title-link">
                <Typography
                  content={title}
                  variant={'headingMd'}
                  mobileVariant={config?.simplifiedView && 'headingSm'}
                  responsive={config?.simplifiedView}
                  fontWeight="medium"
                  data-testid="related-article-card-title"
                />
              </LinkItem>
            )}
          </Grid>
        )}
        {!config?.hiddenDescription && (
          <Grid
            size={{ xs: 12 }}
            sx={{ mb: 1 }}
            className={config?.simplifiedView ? 'pt-[18px]' : undefined}
          >
            <Typography
              content={metaDescription}
              variant="bodySm"
              data-testid="related-article-card-description"
            />
          </Grid>
        )}
      </>
    )
    return (
      <Grid
        key={`releated-article-${index}`}
        container
        spacing={1}
        className="max-w-[335px] lg:max-w-[initial]"
        data-testid={`related-article-carousel-card-${index}`}
      >
        {unifiedArticleLink ? (
          <Grid size={{ xs: 12 }}>
            <LinkItem
              data-testid="related-article-card-content-link"
              className="group/card block w-full"
              underline="none"
            >
              <Grid container spacing={1}>
                {cardContent}
              </Grid>
            </LinkItem>
          </Grid>
        ) : (
          cardContent
        )}
        {authorName && (
          <Grid size={{ xs: 12 }} data-testid="related-article-card-author-container">
            <AuthorAvatar
              authorPhoto={authorPhoto}
              index={0}
              href={
                `${FdrPage === siteEnv ? AUTHORS_ROUTE_FDR : AUTHORS_ROUTE}/` +
                (authorSlug || getSlug(authorName))
              }
              authorName={authorName}
              preload={false}
            />
          </Grid>
        )}
      </Grid>
    )
  }
  const relatedArticlesList = useMemo(() => withUuid(relatedArticles), [relatedArticles])
  const titleStyle = config?.titleStyle || ''

  return (
    <Grid
      container
      item
      size={{ xs: 12 }}
      gap={2}
      paddingTop={config?.simplifiedView ? 6 : 4}
      paddingBottom={config?.simplifiedView ? 6 : 4}
    >
      {title && (
        <Grid size={{ xs: 12 }} marginBottom={2}>
          <Typography
            content={title}
            responsive={config?.simplifiedView}
            variant={'headingMd'}
            mobileVariant={config?.simplifiedView ? 'displaySm' : 'headingMd'}
            className={titleStyle}
            component={titleComponent}
            data-testid="article-list-carousel-title"
          />
        </Grid>
      )}
      <Grid size={{ xs: 12 }} display={{ xs: 'block', lg: 'none' }} data-testid="dot-container">
        <Carousel
          carouselItems={relatedArticles}
          className="p-0"
          classNameContainer={'gap-6 first:pl-[2px]'}
          classNameItems="max-w-[335px]"
          itemComponent={RelatedArticleCard}
          variant="standard"
          idPrefix={`releated-articles`}
          withIndicators={true}
          variationDot={config?.dotsOn ?? null}
          classNameIndicatorDots={
            unifiedArticleLink
              ? undefined
              : config?.dotsOn === 'onWhite'
              ? 'bg-[#3300ff]'
              : 'bg-[#3300ff] mt-8'
          }
          classNameIndicators={
            unifiedArticleLink ? '[&>div]:bg-[#c0cbd8] [&>div]:opacity-100' : undefined
          }
        />
      </Grid>
      <Grid size={{ xs: 12 }} display={{ xs: 'none', lg: 'block' }} data-testid="dot-container">
        <Grid container spacing={4} data-testid="article-cards-desktop">
          {relatedArticlesList.map((item, index) => {
            return (
              <Grid size={{ xs: 4 }} key={item.uuid} data-testid="article-card">
                <RelatedArticleCard item={item} index={index} />
              </Grid>
            )
          })}
        </Grid>
      </Grid>
      {linkCta && (
        <Grid size={{ xs: 12 }} marginBottom={2} className="flex justify-center mt-8 mb-0 mx-0">
          <LinkButton
            size="large"
            content={linkCta}
            data-testid="article-cta"
            typographicOptions={{ variant: 'labelMd' }}
            className="w-full lg:w-auto"
          />
        </Grid>
      )}
      {link && (
        <Grid
          size={{ xs: 12 }}
          marginBottom={2}
          className="flex justify-center mt-[10px] mb-0 mx-0"
        >
          <AchieveLink
            href={link.linkHref}
            track={{
              ...{
                list_name: `ARTICLE LINK`,
                click_id: link?.linkText?.json?.content[0]?.content[0]?.value,
                click_text: link?.linkText?.json.content[0]?.content[0]?.value,
                nav_link_section: 'Article',
                track_event: 'link_click',
                click_type: 'Link Click',
                event_action: 'link_click',
                event_type: 'link_click',
              },
              ...link?.event,
            }}
            // fontWeight="bold"
            // variant="bodySm"
            className={'flex justify-center mt-6 headingSm'}
            underline="hover"
            data-testid="ArticlesCarousel_link"
          >
            {link?.linkText?.json?.content?.[0]?.content[0]?.value}
          </AchieveLink>
        </Grid>
      )}
    </Grid>
  )
}

export { ArticleListCarousel }
export default ArticleListCarousel
